import React, { useEffect, useState } from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';
import { shareService } from '../services/shareService';
import { formatDate } from '../lib/format';
import { PublicDownload, ShareLink } from '../types';
import { EmptyState, ErrorNote, LoadingState, Modal } from './ui';

interface PublicDownloadsModalProps {
  link: ShareLink;
  releaseVersions: Map<string, string>;
  onClose: () => void;
}

const PublicDownloadsModal: React.FC<PublicDownloadsModalProps> = ({ link, releaseVersions, onClose }) => {
  const { t, language } = useLanguage();
  const [downloads, setDownloads] = useState<PublicDownload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    shareService
      .getPublicDownloads(link)
      .then((data) => !cancelled && setDownloads(data))
      .catch((err) => {
        console.error('Error loading public downloads:', err);
        if (!cancelled) setError(true);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [link]);

  return (
    <Modal title={t('share.visitorsTitle')} description={t('share.visitorsText')} onClose={onClose} size="xl">
      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorNote>{t('downloads.loadFailed')}</ErrorNote>
      ) : downloads.length === 0 ? (
        <EmptyState icon={UserIcon} title={t('downloads.empty')} text={t('share.visitorsEmpty')} compact />
      ) : (
        <div className="-mx-6 overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th className="pl-6">{t('downloads.who')}</th>
                <th>{t('share.contact')}</th>
                <th>{t('downloads.what')}</th>
                <th>{t('downloads.ip')}</th>
                <th className="pr-6">{t('downloads.when')}</th>
              </tr>
            </thead>
            <tbody>
              {downloads.map((d) => (
                <tr key={d.id}>
                  <td className="pl-6 font-medium text-gray-900 dark:text-gray-100">{d.visitorName}</td>
                  <td>
                    <a href={`mailto:${d.visitorEmail}`} className="block text-primary-600 hover:underline dark:text-primary-400">
                      {d.visitorEmail}
                    </a>
                    <a href={`tel:${d.visitorPhone}`} className="block text-xs text-gray-500 hover:underline">
                      {d.visitorPhone}
                    </a>
                  </td>
                  <td>
                    {releaseVersions.get(d.releaseId) ? (
                      <span className="version-tag">v{releaseVersions.get(d.releaseId)}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="whitespace-nowrap font-mono text-xs">{d.ipAddress || '—'}</td>
                  <td className="whitespace-nowrap pr-6">{formatDate(d.downloadedAt, language, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

export default PublicDownloadsModal;
